import type {
  ExtendedConstraints,
  CapabilityConfig,
  Message,
} from '../../types';
import { hasOnlyKeysFromSet, hasValue } from '../../utils';

export const id = 'notification';

export enum Events {
  CustomEvent = 'notification:custom-event',
}

export type CustomEventPayload = unknown;

export enum Actions {
  Snackbar = 'notification:snackbar',
  Toast = 'notification:toast',
  Dismiss = 'notification:dismiss',
}

/**
 * @deprecated please use NotificationIcon instead.
 */
export enum NotificationType {
  Success = 'success',
  Info = 'info',
  Warning = 'warning',
  Error = 'error',
  Loading = 'loading',
}

export enum NotificationIcon {
  Success = 'success',
  Info = 'info',
  Warning = 'warning',
  Error = 'error',
  Loading = 'loading',
  Scribe = 'scribe',
  Transcription = 'transcription',
  Medal = 'medal',
  Trophy = 'trophy',
  Cross = 'cross',
}

export type NotificationPayloadBase = {
  title: string;
  icon?: NotificationIcon;
  id?: string;
};

export type NotificationSnackbarPayload = NotificationPayloadBase & {
  buttonType?: string;
  buttonText?: string;
  isCloseable?: boolean;
  callback?: Message;
};

export type NotificationToastPayload = NotificationPayloadBase;

export type DismissPayload = {
  id: string;
};

export type Config = CapabilityConfig & {
  type: typeof id;
  categories: NotificationCategory[];
};

export type Constraints = ExtendedConstraints<{
  events: Events;
  actions: Actions;
  config: Config;
  actionPayloadTuple:
    | [Actions.Snackbar, NotificationSnackbarPayload]
    | [Actions.Toast, NotificationToastPayload]
    | [Actions.Dismiss, DismissPayload];
  eventPayloadTuple: [Events.CustomEvent, CustomEventPayload];
  eventHandlerTuple:
    | [Events.CustomEvent, (payload: CustomEventPayload) => void];
}>;

export enum NotificationCategory {
  Snackbar = 'Snackbar',
  Toast = 'Toast',
}
const categoryValues = Object.values(NotificationCategory) as string[];

const VALID_CONFIG_KEYS = ['type', 'categories'] as const;
export function isValidConfig(config: CapabilityConfig): config is Config {
  if (!hasOnlyKeysFromSet(config, VALID_CONFIG_KEYS)) {
    return false;
  }

  if (config.type !== id) {
    return false;
  }

  // Validate categories array
  const isCategoriesNotValid =
    !hasValue(config, 'categories') ||
    !Array.isArray(config.categories) ||
    !(config.categories as string[]).every((c) => categoryValues.includes(c));

  if (isCategoriesNotValid) {
    return false;
  }

  return true;
}
